import React, {Fragment} from 'react';
import clsx from 'clsx';
import {LinearProgress} from '@mui/material';
import {type Row, type RowData} from '@tanstack/react-table';
import {type ColumnPinningState} from '@tanstack/table-core';
import {useArrowFocusTrapItem} from '@netx/core-components/ArrowFocusTrapNext';
import {useWhenChangedEffect} from '@netx/core-components/hooks';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {TableBodyRowCell} from './TableBodyRowCell';
import {
	getTableBodyRowUtilityClass,
	TABLE_BODY_ROW_CLASS_NAME,
	tableBodyRowClasses,
	type TableBodyRowClasses,
} from './tableBodyRowClasses';
import {tableCellClasses} from './tableCellClasses';
import {TableRow, type TableRowProps} from './TableRow';

type TableBodyRowOwnerState = WithClasses<
	TableBodyRowClasses,
	{
		flagRow?:
			| 'info'
			| 'warning'
			| 'error'
			| 'success'
			| 'primary'
			| 'secondary';
		isEngaged?: boolean;
		isClickable?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableBodyRowOwnerState) => {
	const {classes, flagRow, isEngaged, isClickable} = ownerState;
	const slots = {
		root: [
			'root',
			isClickable && 'isClickable',
			isEngaged && 'isEngaged',
			flagRow && `flagRow-${flagRow}`,
		],
	};
	return composeClasses(slots, getTableBodyRowUtilityClass, classes);
};

const TableBodyRowRoot = styled(TableRow, {
	name: TABLE_BODY_ROW_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<TableBodyRowOwnerState>) => ({
	background: theme.palette.background.default,
	[`&.${tableBodyRowClasses.isClickable}:not(.disabled)`]: {
		cursor: 'pointer',
	},
	[`&.${tableBodyRowClasses.isEngaged}`]: {
		zIndex: 2,
	},
	'&.disabled': {
		...theme.mixins.disabled,
		pointerEvents: 'none',
	},
	'&:hover:not(.disabled)': {
		[`&, & .${tableCellClasses.root}`]: {
			background: theme.palette.mode === 'light' ? '#f0f0f0' : '#2d2d2d',
		},
	},
	...(ownerState?.flagRow && {
		'&:before': {
			content: '""',
			position: 'absolute',
			bottom: 4,
			top: 4,
			left: 6,
			width: 2,
			backgroundColor: theme.palette[ownerState.flagRow].main,
			opacity: 0.8,
			zIndex: 6,
		},
	}),
	'&.pseudo-disabled': {
		[`& .${tableCellClasses.root}:not(.${tableCellClasses.isUtilityCell})`]: {
			color: theme.palette.text.disabled,
		},
	},
}));

const TableBodyRowDummyCell = styled('div')(({theme}) => ({
	flex: '1 1 auto',
}));

const TableBodyRowProgress = styled(LinearProgress)(({theme}) => ({
	position: 'absolute',
	bottom: 0,
	left: 0,
	right: 0,
	height: 2,
	zIndex: 4,
}));

export interface TableBodyRowProps<TData extends RowData>
	extends
		Omit<TableRowProps<TData>, 'classes'>,
		WithClasses<TableBodyRowClasses> {
	disabled?: boolean;
	index: number;
	isEngaged?: boolean;
	lastSizeableColumnIndex?: number;
	leftLeafColumnWidths?: number[];
	pinnedColumns?: ColumnPinningState;
	rightLeafColumnWidths?: number[];
	progress?: number;
	row: Row<TData>;
	virtualHeight?: number;
	flagRow?: (
		row: Row<TData>,
	) =>
		| 'info'
		| 'warning'
		| 'error'
		| 'success'
		| 'primary'
		| 'secondary'
		| undefined;
	onRowClick?: (event: React.MouseEvent) => void;
}

export const TableBodyRowPrimitive = <TData extends RowData>(
	{
		classes: _classes,
		className,
		disabled,
		isEngaged,
		lastSizeableColumnIndex,
		leftLeafColumnWidths,
		pinnedColumns,
		progress,
		rightLeafColumnWidths,
		row,
		virtualHeight = 0,
		flagRow,
		onClick,
		onRowClick,
		...props
	}: TableBodyRowProps<TData>,
	ref?: React.ForwardedRef<HTMLDivElement>,
) => {
	// we need to use a shared ref to support both the outside and the focus trap hook
	const rootRef = useSharedRef<HTMLDivElement | null>(null, [ref]);

	// setup the arrow focus trap hook
	const arrowFocusTrapItem = useArrowFocusTrapItem({
		index: props.index,
	});

	const topAlignCellContent = virtualHeight > 56;

	const ownerState: TableBodyRowOwnerState = {
		classes: _classes,
		className,
		isClickable: onRowClick != null,
		isEngaged,
		flagRow: flagRow?.(row),
	};

	const classes = useUtilityClasses(ownerState);

	useWhenChangedEffect(
		arrowFocusTrapItem.isActive,
		(isActive) => {
			if (!isActive) return;
			setTimeout(() => {
				rootRef.current?.focus({preventScroll: true});
			}, 0);
		},
		{
			executeAtMount: true,
		},
	);

	return (
		<TableBodyRowRoot
			data-row-id={row.id}
			isSelected={row.getCanSelect() && row.getIsSelected()}
			// {...attributes}
			{...props}
			ownerState={ownerState}
			ref={rootRef}
			className={clsx(classes.root, className, {disabled})}
			{...arrowFocusTrapItem.attributes}
			onClick={onClick}
			onRowClick={onRowClick}
			onMouseDown={splitEventHandler(
				arrowFocusTrapItem.attributes.onMouseDown,
				props.onMouseDown,
			)}
		>
			{row.getVisibleCells().map((cell, index) => {
				return (
					<Fragment key={cell.id}>
						{/**
						 * this dummy cell will fill out any empty space that may be left from column sizing
						 * this is important for the last column to ensure any pinned right columns are aligned correctly
						 * also allows us to fudge numbers / allow underflow to be handled by the browser
						 */}
						{lastSizeableColumnIndex === index && (
							<TableBodyRowDummyCell
								className={tableCellClasses.root}
								aria-hidden="true"
							/>
						)}
						<TableBodyRowCell<TData>
							cell={cell}
							index={index}
							leftLeafColumnWidths={leftLeafColumnWidths}
							pinnedColumns={pinnedColumns}
							rightLeafColumnWidths={rightLeafColumnWidths}
							verticalAlign={topAlignCellContent ? 'baseline' : 'center'}
						/>
					</Fragment>
				);
			})}
			{progress != null && (
				<TableBodyRowProgress
					variant={progress !== Infinity ? 'determinate' : 'indeterminate'}
					value={progress}
				/>
			)}
		</TableBodyRowRoot>
	);
};

/**
 * A row in the table body.
 *
 * @typeParam TData The type of data that represents a row
 *
 * @example
 * ```tsx
 * <TableBodyRow
 * 	index={0}
 * 	row={row}
 * 	onClick={handleRowClick}
 * />
 * ```
 */
export const TableBodyRow = React.memo(
	React.forwardRef(TableBodyRowPrimitive),
	// compareProps(),
) as <TData extends RowData>(props: TableBodyRowProps<TData>) => JSX.Element;

export default TableBodyRow;
